<script lang="ts" setup>
import type { SubjectCode, SubjectViewProps } from '~/models/Subject'
import type { ProductHeaders } from '~/models/ProductHeaders'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { KsSkeleton, KsSkeletonWrapper } from '@aschehoug/kloss'
import { lowerSecondaryGrades } from '~/utils/gradeSorter'
import { colorMap, getColorAndShade } from '~/utils/colors'
import useSubjectsStore from '~/stores/subjects'
import useProductStore from '~/stores/product'
import useFilterStore from '~/stores/filter'
import { useAuthStore } from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import TeacherButton from '~/components/subject/TeacherButton.vue'
import SubjectSearch from '~/components/subject/SubjectSearch.vue'
import ColophonButton from '~/components/subject/ColophonButton.vue'
import LegacySubjectButton from '~/components/ResourceFinder/LegacySubjectButton.vue'
import GradeFilter from '~/components/ResourceFinder/GradeFilter.vue'
import TransparentCard from '~/components/cards/TransparentCard.vue'

const props = defineProps<SubjectViewProps>()

const { t } = useI18n()
const router = useRouter()
const { selectedGrade } = storeToRefs(useFilterStore())
const { isTeacher } = storeToRefs(useAuthStore())
const { hasLoaded: hasLoadedProducts } = storeToRefs(useProductStore())
const { currentSubject } = storeToRefs(useSubjectsStore())
const { setCurrentSubject } = useSubjectsStore()
const { getProductHeadersBySubjectAndGrade } = useContentApi()
const { clear: clearAppColor, set: setAppColor } = useAppColor()

const showTeacherButton = computed(() => isTeacher.value
  && hasLoadedProducts.value
  && selectedGrade.value
  && props.subjectCode
)

const hideGradeFilter = computed(() => props.subjectCode === 'NOR'
  && selectedGrade.value
  && lowerSecondaryGrades.includes(selectedGrade.value))

const headers = computed(() => {
  if (!data?.value)
    return []
  const key: keyof ProductHeaders = props.isUpcoming ? 'upcomingContent' : 'learningPath'
  return data.value[key]
})

const hasArchive = computed(() => !!data?.value?.archivedContent?.length)

const archiveUri = computed(() => {
  if (props.isUpcoming) {
    return router.resolve({ name: 'subject', params: { subject: props.subjectCode.toLowerCase() } }).href
  }
  if (hasArchive.value) {
    return router.resolve({ name: 'subject_archive', params: { subject: props.subjectCode.toLowerCase() } }).href
  }
  return null
})

const { data, isLoading } = useQuery({
  enabled: computed(() => !!selectedGrade.value),
  queryKey: computed(() => ['subject-headers', props.subjectCode, selectedGrade.value]),
  queryFn: () => getProductHeadersBySubjectAndGrade(props.subjectCode, selectedGrade.value!),
  staleTime: Infinity,
})

const themeColor = computed(() => {
  if (!currentSubject.value)
    return 'Blue'
  return ({
    SAF: 'Green',
    ENG: 'Blue',
    NOR: 'Green',
    KRL: 'Coral',
    SPA: 'Coral'
  } as Partial<Record<SubjectCode, string>>)[currentSubject.value] ?? 'Blue'
})

const themeColorValue = computed(() => getColorAndShade(themeColor.value, '60'))
const skeletonColor = computed(() => colorMap.get(getColorAndShade(themeColor.value, '50'))?.rgb)
const textColor = computed(() => colorMap.get(getColorAndShade(themeColor.value, '5'))?.rgb)

onMounted(() => setAppColor(themeColorValue.value))

onBeforeUnmount(() => setCurrentSubject(undefined))

// Update app color when changing subject from dropdown
watch(themeColor, () => {
  if (!themeColor.value)
    return
  clearAppColor()
  setAppColor(themeColorValue.value)
})

watch(props, () => {
  if (!props.subjectCode)
    return
  setCurrentSubject(props.subjectCode)
}, { immediate: true })

watch(selectedGrade, () => {
  router.replace({
    name: props.isUpcoming ? 'subject_upcoming' : 'subject',
    params: {
      subject: props.subjectCode.toLowerCase(),
    },
    query: {
      grade: selectedGrade.value
    }
  })
})
</script>

<template>
  <div class="relative mx-auto mb-20 size-full max-w-screen-au px-4 sm:px-8" :style="{ color: textColor }">
    <div class="mx-auto mb-16 flex-1 space-y-8 pt-28 transition-all">
      <section class="flex w-full items-center gap-6">
        <h1 class="text-4xl font-extrabold leading-none" v-text="t(`metadata.subjects.${subjectCode}`)" />
        <GradeFilter
          v-if="!hideGradeFilter"
          shape="normal"
          class="mt-1"
        />
      </section>

      <section class="space-y-4">
        <div class="flex items-end justify-between">
          <h1
            class="font-bold fluid-text-lg"
            v-text="t('filters.chooseHeader')"
          />
          <div
            class="flex items-center gap-4"
            style="--ks-border: rgb(var(--au-yellow-30)); color: rgb(var(--au-yellow-30)); --ks-borderhoverfill: rgb(var(--au-yellow-30))"
          >
            <LegacySubjectButton
              v-if="archiveUri"
              :uri="archiveUri"
            />
            <TeacherButton
              v-if="showTeacherButton && selectedGrade"
              :subject-code="subjectCode"
              :grade-code="selectedGrade"
              :data-pendo="PendoFeature.TeacherContent.Subject"
            />
            <ColophonButton
              v-if="hasLoadedProducts && selectedGrade"
              :subject-code="subjectCode"
              :grade-code="selectedGrade"
              :data-pendo="PendoFeature.Colophon.Subject"
            />
          </div>
        </div>
        <KsSkeletonWrapper
          v-if="isLoading"
          class="grid w-full grid-cols-1 gap-7 sm:grid-cols-2 md:grid-cols-3"
        >
          <KsSkeleton
            v-for="i in 6"
            :key="`skeleton-${i}`"
            height="250px"
            :background="skeletonColor"
          />
        </KsSkeletonWrapper>
        <ul
          v-else-if="selectedGrade"
          class="grid w-full max-w-screen-au grid-cols-1 gap-7 sm:grid-cols-2 md:grid-cols-3"
        >
          <li
            v-for="content in headers"
            :key="`card-${content.locationId}`"
          >
            <TransparentCard
              :resource="content"
              :theme-color="themeColor"
              :is-upcoming="isUpcoming"
              :subject-code="subjectCode"
              :grade-code="selectedGrade"
              loading="eager"
            />
          </li>
        </ul>
      </section>
    </div>
    <SubjectSearch
      v-if="hasLoadedProducts && selectedGrade"
      :subject-code="subjectCode"
      :grade-code="selectedGrade"
      :theme-color="themeColor"
    />
  </div>
</template>
